import React, { useState, useEffect } from 'react';
import { FaFolderPlus, FaChevronRight, FaChevronDown, FaEdit, FaTrash, FaFileAlt, FaFile } from 'react-icons/fa';
import Select from 'react-select';
import { getCommunicationTemplates, createCommunicationTemplate, updateCommunicationTemplate, deleteCommunicationTemplate } from '../../api/communicationTemplates';

const SMSTemplates = () => {
  // Company selection logic
  let companyOptions = [];
  const companiesStr = localStorage.getItem('companies');
  if (companiesStr) {
    try {
      const companies = JSON.parse(companiesStr);
      if (Array.isArray(companies) && companies.length > 0) {
        companyOptions = [
          ...companies.map(c => ({
            value: c.id,
            label: c.company_name
          }))
        ];
      }
    } catch (e) {
      companyOptions = [];
    }
  }
  const [selectedCompany, setSelectedCompany] = useState('');

  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [newTemplateName, setNewTemplateName] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateContent, setTemplateContent] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [errors, setErrors] = useState({});

  // Fetch templates from API on component mount or company change
  useEffect(() => {
    // Only fetch templates if either no company options (not super admin) or company is selected
    if (companyOptions.length === 0 || selectedCompany) {
      fetchTemplates();
    }
  }, [selectedCompany]);

  const fetchTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = { template_type: 'sms' };
      // Always pass company_id - either from selectedCompany or empty string
      payload.company_id = selectedCompany ? selectedCompany.value : '';
      const response = await getCommunicationTemplates(payload);
      if (response.code) {
        // Add isOpen property to folders while preserving the hierarchical structure
        const templatesWithState = response.data.map(item => ({
          ...item,
          isOpen: false
        }));
        setFolders(templatesWithState);
      } else {
        setError(response.message || 'Failed to fetch templates');
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Error fetching templates');
    } finally {
      setLoading(false);
    }
  };

  const toggleFolder = (folderId) => {
    const toggleRecursive = (items) => {
      return items.map(item => {
        if (item.id === folderId) {
          return { ...item, isOpen: !item.isOpen };
        }
        if (item.subfolders) {
          return { ...item, subfolders: toggleRecursive(item.subfolders) };
        }
        return item;
      });
    };
    setFolders(toggleRecursive(folders));
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      const payload = {
        name: newFolderName,
        type: 'folder',
        template_type: 'sms',
        parent_id: selectedFolderId || null,
        created_by: 1 // This should come from auth context
      };
      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }
      const response = await createCommunicationTemplate(payload);

      if (response.code) {
        // Refresh the templates list
        await fetchTemplates();
        setNewFolderName('');
        setShowCreateFolder(false);
        setSelectedFolderId(null);
      } else {
        setError(response.message || 'Failed to create folder');
      }
    } catch (err) {
      console.error('Error creating folder:', err);
      setError('Error creating folder');
    }
  };

  const createTemplate = async () => {
    if (!newTemplateName.trim()) return;

    try {
      const payload = {
        name: newTemplateName,
        type: 'template',
        template_type: 'sms',
        parent_id: selectedFolderId || null,
        created_by: 1 // This should come from auth context
      };
      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }
      const response = await createCommunicationTemplate(payload);

      if (response.code) {
        // Refresh the templates list
        await fetchTemplates();
        setNewTemplateName('');
        setShowCreateTemplate(false);
        setSelectedFolderId(null);
      } else {
        setError(response.message || 'Failed to create template');
      }
    } catch (err) {
      console.error('Error creating template:', err);
      setError('Error creating template');
    }
  };

  const deleteItem = async (itemId, isTemplate = false) => {
    try {
      const payload = { template_type: 'sms' };
      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }
      const response = await deleteCommunicationTemplate(itemId, payload);
      if (response.code) {
        // Refresh the templates list
        await fetchTemplates();
      } else {
        setError(response.message || 'Failed to delete item');
      }
    } catch (err) {
      console.error('Error deleting item:', err);
      setError('Error deleting item');
    }
  };

  const editTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateContent(template.content || '');
    setEmailSubject(template.subject || '');
    setErrors({});
  };

  const saveTemplate = async () => {
    const newErrors = {};

    if (!templateContent.trim()) {
      newErrors.templateContent = 'SMS content is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const payload = {
        content: templateContent,
        updated_by: 1 // This should come from auth context
      };
      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }
      const response = await updateCommunicationTemplate(editingTemplate.id, payload);

      if (response.code) {
        // Refresh the templates list
        await fetchTemplates();
        setEditingTemplate(null);
        setTemplateContent('');
        setEmailSubject('');
        setErrors({});
      } else {
        setError(response.message || 'Failed to save template');
      }
    } catch (err) {
      console.error('Error saving template:', err);
      setError('Error saving template');
    }
  };

  const renderFolderTree = (items, level = 0) => {
    return items.map(item => {
      const paddingLeft = level * 50;
      const isSubfolder = level > 0;
      const hasChildren = item.subfolders && item.subfolders.length > 0;

      return (
        <div key={item.id} className="folder-item">
          <div
            className="d-flex align-items-center py-2 px-2 hover-bg-light rounded cursor-pointer"
            style={{ paddingLeft: `${paddingLeft + 8}px` }}
          >
            <button
              onClick={() => toggleFolder(item.id)}
              className="btn btn-sm btn-link mr-2 p-1"
            >
              {hasChildren ? (
                item.isOpen ? <FaChevronDown size={16} /> : <FaChevronRight size={16} />
              ) : (
                <div style={{ width: 16 }} />
              )}
            </button>
            <FaFolderPlus size={isSubfolder ? 15 : 25} className="text-primary mr-3" />
            <span
              className="flex-grow-1"
              style={{
                fontWeight: isSubfolder ? 'normal' : 'bold',
                fontSize: isSubfolder ? '14px' : '16px',
                marginLeft:'10px'
              }}
            >
              {item.name}
            </span>
            <div className="d-flex gap-2">
              {item.type === 'folder' && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFolderId(item.id);
                      setShowCreateFolder(true);
                    }}
                    className="btn btn-sm btn-link p-1"
                    title="Add Subfolder"
                  >
                    <FaFolderPlus size={16} className="text-secondary" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFolderId(item.id);
                      setShowCreateTemplate(true);
                    }}
                    className="btn btn-sm btn-link p-1"
                    title="Add Template"
                  >
                    <FaFile size={16} className="text-secondary" />
                  </button>
                </>
              )}
              {item.type === 'template' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    editTemplate(item);
                  }}
                  className="btn btn-sm btn-link p-1"
                  title="Edit"
                >
                  <FaEdit size={16} className="text-secondary" />
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  deleteItem(item.id, item.type === 'template');
                }}
                className="btn btn-sm btn-link p-1 text-danger"
                title="Delete"
              >
                <FaTrash size={16} />
              </button>
            </div>
          </div>

          {item.isOpen && hasChildren && (
            <div className="subfolders">
              {renderFolderTree(item.subfolders, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <article className="col-lg-9 articleClass">
      <div className="article-body store-bd-gy">
        <div className="row align-items-center mb-4">
          <div className="col-sm-12">
            <h3 className="page-title">SMS Templates</h3>
            <p className="text-muted">Manage your SMS templates in folders and subfolders</p>
          </div>
        </div>

        {/* Company Selection - Only show if there are company options */}
        {companyOptions.length > 0 && (
          <div className="row mb-4">
            <div className="col-md-6">
              <label className="form-label">Select Company</label>
              <Select
                options={companyOptions}
                value={selectedCompany}
                onChange={(selected) => {
                  setSelectedCompany(selected);
                }}
                placeholder="Select Company"
              />
            </div>
          </div>
        )}

        {/* Show company selection message if no company is selected */}
        {companyOptions.length > 0 && !selectedCompany && (
          <div className="alert alert-info mb-4">
            Please select a company to view and edit SMS templates.
          </div>
        )}

        <div className="card">
          <div className="card-body">
          {error && (
            <div className="alert alert-danger mb-3">
              {error}
              <button type="button" className="btn-close float-end" onClick={() => setError(null)}></button>
            </div>
          )}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="card-title mb-0">Template Structure</h5>
            <div className="d-flex gap-2">
              <button
                onClick={() => {
                  setSelectedFolderId(null);
                  setShowCreateFolder(true);
                }}
                className="btn btn-primary d-flex align-items-center gap-2"
              >
                <FaFolderPlus size={16} />
                New Folder
              </button>
              <button
                onClick={() => {
                  setSelectedFolderId(null);
                  setShowCreateTemplate(true);
                }}
                className="d-none btn btn-success d-flex align-items-center gap-2"
              >
                <FaFile size={16} />
                New Template
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : folders.length === 0 ? (
            <div className="text-center py-12 text-muted">
              No folders yet. Create a folder to get started.
            </div>
          ) : (
            <div className="folder-tree">
              {renderFolderTree(folders)}
            </div>
          )}
        </div>
      </div>

      {/* Create Folder Modal */}
      {showCreateFolder && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {selectedFolderId ? 'Create Subfolder' : 'Create Folder'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCreateFolder(false);
                    setNewFolderName('');
                    setSelectedFolderId(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Folder name"
                  className="form-control"
                  autoFocus
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCreateFolder(false);
                    setNewFolderName('');
                    setSelectedFolderId(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={createFolder}
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Template Modal */}
      {showCreateTemplate && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {selectedFolderId ? 'Create Template in Folder' : 'Create Template'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCreateTemplate(false);
                    setNewTemplateName('');
                    setSelectedFolderId(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <input
                  type="text"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  placeholder="Template name"
                  className="form-control"
                  autoFocus
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCreateTemplate(false);
                    setNewTemplateName('');
                    setSelectedFolderId(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={createTemplate}
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Template Modal with Text Editor */}
      {editingTemplate && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content" style={{ height: '80vh' }}>
              <div className="modal-header">
                <h5 className="modal-title">Edit Template: {editingTemplate.name}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setEditingTemplate(null);
                    setTemplateContent('');
                    setEmailSubject('');
                    setErrors({});
                  }}
                ></button>
              </div>
              <div className="modal-body d-flex flex-column" style={{ flex: 1, overflow: 'hidden' }}>
                <div className="mb-3 flex-grow-1 d-flex flex-column">
                  <label className="form-label fw-bold">
                    SMS Content <span className="text-danger">*</span>
                  </label>
                  {errors.templateContent && (
                    <div className="text-danger mb-2">
                      {errors.templateContent}
                    </div>
                  )}
                  <textarea
                    value={templateContent}
                    onChange={(e) => setTemplateContent(e.target.value)}
                    className={`form-control ${errors.templateContent ? 'is-invalid' : ''}`}
                    placeholder="Enter SMS content"
                    rows="5"
                    style={{ minHeight: '150px', resize: 'vertical' }}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setEditingTemplate(null);
                    setTemplateContent('');
                    setEmailSubject('');
                    setErrors({});
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={saveTemplate}
                >
                  Save Template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </article>
  );
};

export default SMSTemplates;
